import { useEffect, useRef, useState } from 'react'
import { prefersReducedMotion } from '../../scoreCountUp'

/** One 590ms flash per card (140ms attack + 450ms decay) — must match the
 *  `ignite` keyframes in src/styles/ignite.css. */
export const IGNITE_DURATION_MS = 590
/** Row-to-row delay. Per ROW, not per card: both cards in a row fire together,
 *  so a 5-row gameweek runs 500/580/660/740/820ms after arrival and the quest
 *  stack (one more row) lands at 900ms. */
export const IGNITE_ROW_STAGGER_MS = 80
/** Held before the first row fires. Landing on the page and having the wave go
 *  off in the same beat read as a flicker — the grid should be settled and read
 *  as still for a moment before anything lights up. */
export const IGNITE_START_DELAY_MS = 500
/** How long a fired wave suppresses the next one for the same gameweek. Long
 *  enough that tabbing to Leagues and back, or editing a score, never replays
 *  it; short enough that a real return to the app later does. */
export const IGNITE_REENTRY_WINDOW_MS = 90_000

/** Per-card hue offset, so the ring's colour distribution differs down the
 *  grid rather than ten identical rings. Index is continuous across the whole
 *  wave: fixtures first, then the quest cards. */
export function igniteAngleFor(index: number): string {
  return `${index * 34}deg`
}

/** Last time the wave actually played, per gameweek. Module-level on purpose:
 *  it has to outlive the grid being unmounted and remounted (tab switch, or a
 *  modal close that re-keys it), which is exactly what the re-entry window is
 *  there to catch. It does not need to survive a page reload. */
const lastFiredAt = new Map<number, number>()

/** Test-only: forget every gameweek's last-fired stamp. */
export function __resetEntryIgniteForTests() {
  lastFiredAt.clear()
}

const NO_ROWS: ReadonlySet<number> = new Set()

/**
 * Whether a wave would play for `gameweek` if one were asked for right now — the
 * reduced-motion opt-out and the re-entry window, and nothing mount-scoped.
 *
 * Exported because the wave is no longer the only thing that moves with it: the entry dock
 * at the foot of the grid arrives on the same beat, and has to know at its own mount whether
 * there is a wave to arrive with. Read it once, at mount: the hook stamps the gameweek as
 * fired moments later, so a later read comes back false mid-animation.
 */
export function igniteWaveWouldPlay(gameweek: number): boolean {
  if (prefersReducedMotion()) return false
  const lastFired = lastFiredAt.get(gameweek)
  return lastFired === undefined || Date.now() - lastFired >= IGNITE_REENTRY_WINDOW_MS
}

interface EntryIgniteOptions {
  gameweek: number
  /** How many rows the wave covers — fixture rows plus, when the quest stack
   *  is on screen, one more for it. */
  rowCount: number
  /** Every trigger condition the grid can answer: the gameweek is fully
   *  predicted, the deadline is still open, and no fixture is live or settled.
   *  May start false and flip true once predictions load. */
  eligible: boolean
}

/**
 * The entry ignite wave — fires the one-shot ring flash across
 * the grid, one row at a time, top to bottom.
 *
 * It plays at most once per mount AND at most once per 90s per gameweek. The
 * per-mount half is what makes editing a score safe: `eligible` is watched
 * (not just mount) so an async predictions load still gets a wave, but once
 * the wave has run, nothing that happens while the grid stays mounted can
 * start another. The per-gameweek half covers the remount cases — closing the
 * prediction modal, or bouncing off to another tab and back.
 *
 * Returns the set of row indices currently lit; a card asks about its own row.
 */
export function useEntryIgnite({ gameweek, rowCount, eligible }: EntryIgniteOptions) {
  const [ignitedRows, setIgnitedRows] = useState<ReadonlySet<number>>(NO_ROWS)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  // Flipped by the first row actually firing, not by scheduling. Two things
  // hang off that distinction: a wave that was scheduled and then torn down
  // before it painted (React's StrictMode double-mount in dev) leaves no trace
  // and is free to schedule again, and the 90s stamp only records waves the
  // user could have seen.
  const startedRef = useRef(false)

  // Unmount only — a wave in flight must survive prop changes (a quest pick
  // landing mid-wave changes rowCount, say), so the trigger effect below
  // deliberately does not clear timers when its deps change.
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
    },
    []
  )

  useEffect(() => {
    if (startedRef.current || !eligible || rowCount === 0) return
    if (!igniteWaveWouldPlay(gameweek)) return

    // Re-running before the wave painted (deps changed in the same tick, or
    // StrictMode remounted us) reschedules from scratch rather than stacking a
    // second set of timers on top of the first.
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []

    for (let row = 0; row < rowCount; row++) {
      timersRef.current.push(
        setTimeout(() => {
          if (row === 0) {
            startedRef.current = true
            lastFiredAt.set(gameweek, Date.now())
          }
          setIgnitedRows((lit) => new Set(lit).add(row))
          // Dropping the class again is what lets a later wave re-add it and
          // get a fresh animation rather than a dead one.
          timersRef.current.push(
            setTimeout(() => {
              setIgnitedRows((lit) => {
                const next = new Set(lit)
                next.delete(row)
                return next
              })
            }, IGNITE_DURATION_MS)
          )
        }, IGNITE_START_DELAY_MS + row * IGNITE_ROW_STAGGER_MS)
      )
    }
  }, [eligible, gameweek, rowCount])

  return ignitedRows
}
